import yaml from "js-yaml";
import { SkillFrontmatterSchema, type PlannerSkill } from "@amazing-wedding/shared-types";

const FRONTMATTER_RE = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/;

/**
 * Parse a single skill `.md` file from its raw text. The YAML frontmatter is
 * validated against `SkillFrontmatterSchema`; the body is returned as the
 * raw markdown that will be injected into the chat session.
 *
 * Throws when the frontmatter is missing or fails validation. Callers wrap
 * the error with the source path so authoring mistakes surface at the
 * exact file at build time.
 */
export function parseSkillRaw(rawContent: string, sourcePath: string): PlannerSkill {
  const match = rawContent.match(FRONTMATTER_RE);
  if (!match) {
    throw new Error(
      `Skill ${sourcePath} is missing YAML frontmatter — wrap the metadata in --- delimiters.`,
    );
  }
  const frontmatterBlock = match[1] ?? "";
  const body = (match[2] ?? "").trim();

  let parsedFrontmatter: unknown;
  try {
    parsedFrontmatter = yaml.load(frontmatterBlock);
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    throw new Error(`Invalid YAML frontmatter in ${sourcePath}: ${message}`);
  }

  const result = SkillFrontmatterSchema.safeParse(parsedFrontmatter);
  if (!result.success) {
    const issue = result.error.issues[0];
    const path = issue?.path.join(".") ?? "<root>";
    throw new Error(
      `Invalid skill frontmatter in ${sourcePath}: ${path} — ${issue?.message ?? "unknown"}`,
    );
  }
  if (!body) {
    throw new Error(`Skill ${sourcePath} has empty body — provide an instruction block.`);
  }
  return { frontmatter: result.data, body };
}
